import React, { useEffect, useState } from "react";
import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import PageHeader from "../PageHeader";
import axios from "axios";
import Cookies from "js-cookie";
import { triggerAlert } from "../../utils/commonFunctions";
import Loader from "../../common/Loader";

/**
 * Settings > Payment Behavior
 *
 * Deployment-wide billing knobs. First knob: AmEx card surcharge (the
 * ~4% cost most card processors pass through). When enabled, every
 * charge routed through an AmEx card gets the configured percent
 * added on top — subscribes, add-packs, reactivate, phone-number buy,
 * and every future ARB renewal (the surcharge is baked into the
 * recurring amount at subscribe time so month-2+ auto-billing stays
 * consistent).
 *
 * Toggling here affects NEW subs only. Existing subs' ARBs keep
 * charging whatever amount they were created with.
 */

const DEFAULT_FORM = {
  amex_surcharge_enabled: false,
  amex_surcharge_percent: "4.00",
};

export default function PaymentBehavior() {
  document.title = "Payment Behavior";
  const mainHeading = "Payment Behavior";
  const heading = "Settings / Payment Behavior";

  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const authHeaders = () => ({
    headers: {
      Authorization: `Bearer ${Cookies.get("admin_user_token")}`,
      "Content-Type": "application/json",
    },
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get("/api/admin/payment-behavior/", authHeaders())
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        setForm({
          amex_surcharge_enabled: !!data.amex_surcharge_enabled,
          amex_surcharge_percent:
            data.amex_surcharge_percent === undefined ||
            data.amex_surcharge_percent === null
              ? "4.00"
              : String(data.amex_surcharge_percent),
        });
      })
      .catch(() => {
        triggerAlert("error", "Oops...", "Failed to load payment behavior settings.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async (e) => {
    e?.preventDefault();
    // Client-side guard — the backend validates too, but stopping here
    // avoids a round-trip on obvious mistakes.
    const pctNum = Number(form.amex_surcharge_percent);
    if (Number.isNaN(pctNum) || pctNum < 0 || pctNum >= 100) {
      triggerAlert("warning", "", "Surcharge percent must be between 0 and 99.99.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        amex_surcharge_enabled: !!form.amex_surcharge_enabled,
        amex_surcharge_percent: pctNum,
      };
      const res = await axios.patch(
        "/api/admin/payment-behavior/",
        payload,
        authHeaders()
      );
      const changed = res.data?.updated_fields || [];
      if (changed.length) {
        triggerAlert(
          "success",
          "",
          `Saved ${changed.length} change${changed.length === 1 ? "" : "s"}. Applies to new subscriptions from now.`
        );
      } else {
        triggerAlert("info", "", "No changes detected.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        "Failed to save payment behavior settings.";
      triggerAlert("error", "Oops...", msg);
    } finally {
      setSaving(false);
    }
  };

  const previewAmount = () => {
    const pct = Number(form.amex_surcharge_percent);
    if (Number.isNaN(pct) || pct <= 0) return null;
    const base = 100;
    const surcharge = (base * pct) / 100;
    return {
      base: base.toFixed(2),
      surcharge: surcharge.toFixed(2),
      total: (base + surcharge).toFixed(2),
      pct: pct.toFixed(2),
    };
  };

  const preview = previewAmount();

  return (
    <div id="layout-wrapper">
      <Header />
      <Sidebar />
      <div className="page-content" style={{ backgroundColor: "#ededed" }}>
        <div className="main-content">
          <PageHeader mainHeading={mainHeading} heading={heading} />
          <div className="container-fluid">
            {loading ? (
              <div className="loader-overlay text-white">
                <Loader />
              </div>
            ) : (
              <form onSubmit={handleSave}>
                <div className="card mb-3">
                  <div className="card-body">
                    <h5 className="card-title mb-3">
                      <i className="bi bi-credit-card-2-front-fill me-2" />
                      AmEx card surcharge
                    </h5>
                    <p className="text-muted small mb-4">
                      When on, every charge routed through an American Express
                      card gets the surcharge percent added on top —
                      subscribes, add-packs, reactivate, phone-number buys, and
                      every future ARB renewal for those subs. Toggling here
                      applies to <strong>new</strong> subscriptions only;
                      existing subs keep whatever recurring amount they
                      already have.
                    </p>

                    <div className="row mb-3">
                      <label className="col-sm-3 col-form-label">
                        Surcharge enabled
                      </label>
                      <div className="col-sm-9">
                        <div className="form-check form-switch pt-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="amex-surcharge-toggle"
                            checked={!!form.amex_surcharge_enabled}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                amex_surcharge_enabled: e.target.checked,
                              }))
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="amex-surcharge-toggle"
                          >
                            {form.amex_surcharge_enabled
                              ? "AmEx cards will be surcharged"
                              : "AmEx cards charged same as any other card"}
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="row mb-3">
                      <label
                        className="col-sm-3 col-form-label"
                        htmlFor="amex-surcharge-pct"
                      >
                        Surcharge percent
                      </label>
                      <div className="col-sm-9">
                        <div className="input-group" style={{ maxWidth: 200 }}>
                          <input
                            id="amex-surcharge-pct"
                            type="number"
                            className="form-control"
                            step="0.01"
                            min="0"
                            max="99.99"
                            value={form.amex_surcharge_percent}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                amex_surcharge_percent: e.target.value,
                              }))
                            }
                            disabled={!form.amex_surcharge_enabled}
                          />
                          <span className="input-group-text">%</span>
                        </div>
                        <div className="form-text">
                          The rate applied on top of the base charge. 4.00
                          matches most US processors' AmEx pass-through cost.
                        </div>
                      </div>
                    </div>

                    {form.amex_surcharge_enabled && preview && (
                      <div className="alert alert-info small mb-0">
                        <strong>Example:</strong> a ${preview.base} charge on
                        an AmEx card will bill{" "}
                        <strong>${preview.total}</strong> ($
                        {preview.base} + ${preview.surcharge} surcharge at{" "}
                        {preview.pct}%).
                      </div>
                    )}
                  </div>
                </div>

                <div className="text-end">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
